import { addDays, format, startOfDay } from 'date-fns';
import { apiService } from '@clarix/api-client';
import type { User } from '@clarix/shared-types';
import type { PeoplePickerPerson } from '@clarix/ui-shared';
import { getApiErrorMessage, normalizeApiError } from '@clarix/utils';
import { QUICK_CREATE_COPY } from './copy';
import type { WireEvent } from './types';

// The top bar's Schedule meeting / Book a room: a few fields posted straight to calendar-api
// (API-CONTRACT §5, §16), never through the calendar remote (root hard rule 1).

export type QuickCreateMode = 'meeting' | 'room';

const base = (slug: string) => `/${slug}/calendar`;

/** Calendar's own picker page size, so the popover offers the same people. */
const PEOPLE_LIMIT = 500;
/** Calendar's default "10 minutes before" for a new meeting. */
const REMINDER_MINUTES = 10;

export const QUICK_CREATE_MINUTES: Record<QuickCreateMode, number> = { meeting: 60, room: 30 };

interface CalendarWire {
  id: string;
  visible: boolean;
}

export interface QuickPersonWire {
  id: string;
  name: string;
  email: string;
}

export interface QuickRoomAvailability {
  room: { id: string; name: string; floor: string | null; capacity: number };
  free: boolean;
}

type AttendeeInput = { user_id: string } | { email: string };

interface QuickEventBody {
  kind: 'meeting';
  title: string;
  calendar_id: string;
  start: string;
  end: string;
  all_day: false;
  time_zone: string;
  attendees: AttendeeInput[];
  reminder_minutes: number;
  room_id?: string;
}

export const quickCreateApi = {
  calendars: (slug: string) => apiService.get<CalendarWire[]>(`${base(slug)}/calendars`),
  people: (slug: string) =>
    apiService.get<QuickPersonWire[]>(`${base(slug)}/people`, { params: { limit: PEOPLE_LIMIT } }),
  rooms: (slug: string, start: string, end: string) =>
    apiService.get<QuickRoomAvailability[]>(`${base(slug)}/rooms/availability`, { params: { start, end } }),
  create: (slug: string, body: QuickEventBody) => apiService.post<WireEvent>(`${base(slug)}/events`, body),
};

export const quickCreateKeys = {
  calendars: (slug: string | null) => ['shell', 'quick-create', 'calendars', slug] as const,
  people: (slug: string | null) => ['shell', 'quick-create', 'people', slug] as const,
  rooms: (slug: string | null, start: string, end: string) =>
    ['shell', 'quick-create', 'rooms', slug, start, end] as const,
};

/** The first calendar the caller shows, else the first (Calendar's own default). */
export function defaultCalendarId(calendars: readonly CalendarWire[] | undefined): string | null {
  const pick = calendars?.find((c) => c.visible) ?? calendars?.[0];
  return pick ? String(pick.id) : null;
}

const toHHmm = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** The next half hour; past 23:00 it is 09:00 tomorrow (Calendar's own default slot). */
export function nextSlot(now: Date): { day: Date; time: string } {
  const start = Math.ceil((now.getHours() * 60 + now.getMinutes() + 1) / 30) * 30;
  return start > 23 * 60 ? { day: addDays(startOfDay(now), 1), time: '09:00' } : { day: startOfDay(now), time: toHHmm(start) };
}

export function slotWindow(day: Date, time: string, minutes: number): { start: Date; end: Date } {
  const [h, m] = time.split(':').map(Number);
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m);
  return { start, end: new Date(start.getTime() + minutes * 60_000) };
}

/** The earliest start offered today, so a past time can't be picked. */
export function minStartToday(day: Date, now: Date): string | undefined {
  return format(day, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd') ? format(now, 'HH:mm') : undefined;
}

export function formatClockAt(at: Date, h24: boolean): string {
  return format(at, h24 ? 'HH:mm' : 'h:mm a');
}

/** The shared picker lists `User` rows and subtitles them with `designation_title`. */
export function personAsUser(p: QuickPersonWire): User {
  return {
    id: String(p.id),
    email: p.email,
    name: p.name || p.email,
    template_id: null,
    template_name: null,
    template_code: null,
    is_workspace_owner: false,
    designation_id: null,
    designation_title: p.email,
    department_id: null,
    department_name: null,
    status_id: null,
    status_name: '',
    app_status: 'ACTIVE',
    source: 'member',
    joined_at: null,
    last_login_at: null,
    created_at: '',
    updated_at: '',
    deleted_at: null,
  };
}

/** Members by id; a typed email (the picker's `external`) as an email invite. */
export function toAttendees(people: readonly PeoplePickerPerson[]): AttendeeInput[] {
  const seen = new Set<string>();
  return people.flatMap((p): AttendeeInput[] => {
    const external = p.type === 'external';
    const key = external ? `e:${p.email.toLowerCase()}` : `u:${p.id}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [external ? { email: p.email.trim().toLowerCase() } : { user_id: p.id }];
  });
}

export function buildEventBody(args: {
  title: string;
  calendarId: string;
  day: Date;
  time: string;
  minutes: number;
  people: readonly PeoplePickerPerson[];
  roomId: string | null;
}): QuickEventBody {
  const { start, end } = slotWindow(args.day, args.time, args.minutes);
  return {
    kind: 'meeting',
    title: args.title.trim(),
    calendar_id: args.calendarId,
    start: start.toISOString(),
    end: end.toISOString(),
    all_day: false,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    attendees: toAttendees(args.people),
    reminder_minutes: REMINDER_MINUTES,
    ...(args.roomId ? { room_id: args.roomId } : {}),
  };
}

export type QuickField = 'name' | 'people' | 'room' | 'time';

/** calendar-api's wire fields → the popover field the message sits under. */
const FIELD_OF: Record<string, QuickField> = {
  title: 'name',
  attendees: 'people',
  co_host_ids: 'people',
  room_id: 'room',
  start: 'time',
  end: 'time',
};

function wireField(details: unknown): string | null {
  if (Array.isArray(details)) {
    const head = (details[0] as { path?: unknown } | undefined)?.path;
    return Array.isArray(head) && typeof head[0] === 'string' ? head[0] : null;
  }
  const field = (details as { field?: unknown } | undefined)?.field;
  return typeof field === 'string' ? field : null;
}

/** What went wrong, worded for the field it belongs to; "Couldn't save" only when nothing more is known. */
export function quickCreateError(error: unknown): { field: QuickField | null; message: string } {
  const { code, details, message, isOffline, isNetwork } = normalizeApiError(error);
  const copy = QUICK_CREATE_COPY;
  if (isOffline || isNetwork) return { field: null, message: copy.offline };
  if (code === 'ROOM_UNAVAILABLE') return { field: 'room', message: copy.roomTaken };
  if (code === 'PAST_START') return { field: 'time', message: copy.pastTime };
  if (code === 'FORBIDDEN') return { field: null, message: copy.forbidden };
  const field = FIELD_OF[wireField(details) ?? ''] ?? null;
  if (code === 'NOT_FOUND' && field === 'room') return { field, message: copy.roomRemoved };
  if (code === 'VALIDATION_ERROR') {
    if (field === 'name') return { field, message: copy.required.name };
    if (field === 'room') return { field, message: copy.required.room };
    return { field, message: message || copy.failed };
  }
  return { field: null, message: getApiErrorMessage(error, copy.failed) };
}

